import React, { useMemo } from "react";
import { check } from "@/helpers/utils";
import { getNumber, getPrimitiveArray } from "@/helpers/validation";
import { shopifyCartApi } from "@/services/shopifyCart";
import { storefrontApi } from "@/services/storefront";
import { catalogSelect } from "@/store";

const SEEN_RECS_KEY = "droppsCartSeenRecommendations";
const RecommendedProductContext = React.createContext<
  | {
      orderHistoryProductIds: number[];
    }
  | undefined
>(undefined);

const readSeenFromStorage = () => {
  try {
    const data = sessionStorage.getItem(SEEN_RECS_KEY) || "[]";
    const storedIdsSetting = JSON.parse(data);
    return getPrimitiveArray(storedIdsSetting, getNumber);
  } catch (ex) {
    return [];
  }
};

const persistSeenToStorage = (seen: number[]) => {
  try {
    sessionStorage.setItem(SEEN_RECS_KEY, JSON.stringify(seen));
  } catch (ex) {}
};

type RecommendedProductProviderProps = {
  orderHistoryProductIds: number[];
  children: React.ReactNode;
};

export const RecommendedProductProvider = ({
  children,
  orderHistoryProductIds,
}: RecommendedProductProviderProps) => {
  return (
    <RecommendedProductContext.Provider value={{ orderHistoryProductIds }}>
      {children}
    </RecommendedProductContext.Provider>
  );
};

export function useRecommendedProducts() {
  const context = check(
    React.useContext(RecommendedProductContext),
    "useRecommended must be used within a RecommendedProvider"
  );
  const orderHistoryProductIds = context.orderHistoryProductIds;
  const shopifyCart = shopifyCartApi.useFetch();

  const recommendedCollection = storefrontApi.useCollection(
    "recommended-products"
  );

  const recommendedProducts = useMemo(() => {
    const storedIds = readSeenFromStorage();
    const catalog = recommendedCollection?.data ?? catalogSelect.default();
    const recommendedProducts = catalogSelect.orderable(catalog);
    const cartItems = shopifyCart.data?.items ?? [];
    const cartVariantIds = new Set(cartItems.map((x) => x.id));
    const cartProducts = recommendedProducts
      .filter((x) => x.variants.some((v) => cartVariantIds.has(v.id)))
      .map((x) => x.id);

    const seenIds = [...storedIds, ...cartProducts, ...orderHistoryProductIds];
    const seenSet = new Set(seenIds);
    persistSeenToStorage([...seenSet]);

    return recommendedProducts
      .filter((x) => !x.title.toLocaleLowerCase().includes("trial"))
      .filter((x) => !seenSet.has(x.id));
  }, [recommendedCollection.data, shopifyCart.data, orderHistoryProductIds]);

  return recommendedProducts;
}
